import { recentWorkers } from "../utils/recentWorkers";
import { loadCandidateList } from "../services/candidateList";
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "../axios/AxiosInstance";
import { expiredDocument } from "../utils/expiredDocument";
import { groupExpiredDocuments } from "../utils/groupExpiredDocuments";
import { apiErrorMessage } from "../utils/apiError";

const ExpiredDocumentsContext = createContext(null);

export function ExpiredDocumentsProvider({ children }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [prefectureCount, setPrefectureCount] = useState(0);
  const [version, setVersion] = useState(0);
  const reload = () => setVersion((current) => current + 1);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    async function load() {
      try {
        const [data, contractResponse] = await Promise.all([
          loadCandidateList().then(data => { if(active) setCandidates(data); return data; }),
          axios.get("pld-contract/freelance", {signal:controller.signal}),
        ]);
        if (!active || controller.signal.aborted) return;
        setCandidates(data);
        const workers = recentWorkers(data, Array.isArray(contractResponse.data) ? contractResponse.data : []);
        let index = 0;
        let failures = 0;
        let reason = null;
        const results = [];
        await Promise.all(Array.from({length:Math.min(2,workers.length)},async () => {
          while (index < workers.length && active && !controller.signal.aborted) {
            const candidate = workers[index++];
            try {
              const {data:documents} = await axios.get(`document/candidate/${candidate._id}`, { signal: controller.signal });
              (Array.isArray(documents) ? documents : []).filter(document => expiredDocument(document)).forEach(document => results.push({candidate,document}));
            } catch (error) {
              if (controller.signal.aborted || error.code === "ERR_CANCELED") return;
              if (error.response?.status !== 404) { failures++; if (!reason) reason = error; }
            }
          }
        }));
        if (active) { setCandidates(data); setRows(results); setError(""); if (failures) setError(`${failures} dossier(s) n’ont pas pu être vérifiés. ${apiErrorMessage(reason, "Actualisez la page pour réessayer.")}`); }
      } catch (error) { const detail = apiErrorMessage(error, "Impossible de charger les candidats et leurs documents."); if (active && detail) setError(detail); }
      finally { if (active && !controller.signal.aborted) setLoading(false); }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [version]);
  useEffect(() => {
    let active = true;
    const refresh = () => axios.get("document/prefecture-controls").then(({data}) => {
      if(active) setPrefectureCount(Array.isArray(data) ? data.filter(row=>row.controlStatus === "required").length : 0);
    }).catch(() => { if(active) setError(current => current || "Les contrôles préfecture n’ont pas pu être comptés."); });
    refresh();
    window.addEventListener("focus",refresh);
    window.addEventListener("jm-prefecture-control-updated",refresh);
    return () => { active = false; window.removeEventListener("focus",refresh); window.removeEventListener("jm-prefecture-control-updated",refresh); };
  }, [version]);
  const dismissDocument = (documentId) => {
    setRows((current) => current.filter((row) => String(row.document?._id) !== String(documentId)));
  };
  const replaceDocument = (documentId, patch) => {
    setRows((current) => current.flatMap((row) => {
      if (String(row.document?._id) !== String(documentId)) return [row];
      const document = { ...row.document, ...patch };
      return expiredDocument(document) ? [{ ...row, document }] : [];
    }));
  };
  const grouped = groupExpiredDocuments(rows);
  const total = grouped.reduce((sum, group) => sum + group.documents.length, 0);
  return <ExpiredDocumentsContext.Provider value={{ loading, error, candidates, setCandidates, grouped, total, prefectureCount, attentionTotal: total + prefectureCount, dismissDocument, replaceDocument, reload }}>{children}</ExpiredDocumentsContext.Provider>;
}

export function useExpiredDocuments() {
  return useContext(ExpiredDocumentsContext);
}
